'use client';

import { cn, tv } from '../_lib/utils';
import MuxPlayer from '@mux/mux-player-react';
import MuxVideo from '@mux/mux-video-react';
import { useEffect, useState } from 'react';
import { Button } from '../Inputs/Button';
import PauseIcon from '../_assets/icons/pause.svg';
import PlayIcon from '../_assets/icons/play.svg';
import { Popup } from '../Popup';

const styles = tv({
	slots: {
		autoplayPlayButton:
			'text-foreground-white self-end col-start-1 row-start-1 justify-self-start z-40 before:content-[""] before:inset-x-0 before:inset-y-0 before:absolute',
		popupPlayButton:
			'bg-background-brand text-foreground-white rounded-full flex items-center justify-center transition-colors duration-normal ease-smooth z-40 hover:bg-background-brand/90',
	},
	variants: {
		buttonSize: {
			sm: {
				autoplayPlayButton: 'm-4',
				popupPlayButton: 'h-12 w-12 m-6 [&_svg]:w-4',
			},
			lg: {
				autoplayPlayButton: 'm-4 md:m-6',
				popupPlayButton: 'h-16 w-16 m-4 md:h-20 md:w-20 md:m-8 lg:h-24 lg:w-24 [&_svg]:w-4 md:[&_svg]:w-6 lg:[&_svg]:w-8',
			},
		},
	},
});

export type VideoPlayerProps = {
	autoplay?: boolean;
	buttonSize?: 'sm' | 'lg';
	className?: string;
	controls?: boolean;
	showPlayButton?: boolean;
	nestedPopup?: boolean;
	playButtonText?: string;
	popup?: boolean;
	video?: string;
	pauseVideo?: boolean;
	setPauseVideo?: Function;
	preload?: boolean;
};

export function VideoPlayer(props: VideoPlayerProps) {
	const buttonSize = props.buttonSize ?? 'lg';
	const controls = props.controls ?? false;
	const playButtonText = props.playButtonText ?? 'Play';
	const showPlayButton = props.showPlayButton ?? true;
	const [videoPlaying, setVideoPlaying] = useState(true);
	const [videoLoaded, setVideoLoaded] = useState(false);
	const { autoplayPlayButton, popupPlayButton } = styles({ buttonSize });
	const muxVideoSettings = {
		autoPlay: true,
		playsInline: true,
		loading: 'viewport',
		playbackId: props.video,
	};

	function playVideo() {
		setVideoLoaded(true);
	}

	useEffect(() => {
		if (props.pauseVideo === false || props.pauseVideo === true) {
			if (props.pauseVideo) {
				setVideoPlaying(true);
			} else {
				setVideoPlaying(false);
			}
		}
	}, [props.pauseVideo]);

	if (!props.video) {
		return;
	}

	return (
		<div className={cn('h-full relative', props.className)} data-component='VideoPlayer'>
			{props.autoplay && (
				<>
					{!controls ? (
						<>
							<div className={`relative h-full aspect-video w-full ${controls ? 'custom-mux-player-controls' : 'mux-hide-controls'}`}>
								<MuxPlayer
									className={`w-full max-w-none h-full object-cover absolute left-0 top-0 ${
										props.pauseVideo === false && !videoPlaying ? 'opacity-0' : 'opacity-100'
									}`}
									autoPlay={videoPlaying}
									controls={false}
									paused={!videoPlaying}
									playsInline={true}
									playbackId={props.video}
									loop={true}
									muted={true}
								/>
							</div>
							{showPlayButton && (
								<div className={`items-center grid h-full left-0 absolute top-0 w-full transition-all z-30`}>
									<Button
										parent='VideoPlayer'
										aria-label={playButtonText}
										className={autoplayPlayButton()}
										onClick={() => setVideoPlaying(!videoPlaying)}
										iconOnly={true}
										styleType='ghost'
										styleSize='small'
									>
										{videoPlaying ? <PauseIcon /> : <PlayIcon />}
									</Button>
								</div>
							)}
						</>
					) : (
						<div className={`relative h-full aspect-video ${controls ? 'custom-mux-player-controls' : 'mux-hide-controls'}`}>
							<MuxPlayer
								className={`w-full max-w-none h-full object-cover`}
								autoPlay={true}
								playsInline={true}
								playbackId={props.video}
								loop={false}
								muted={false}
							/>
						</div>
					)}
				</>
			)}

			{!props.autoplay && (
				<>
					{props.popup ? (
						<Popup
							layout='video'
							onOpenChange={open => props.setPauseVideo?.(!open)}
							triggerButton={
								<div className={`right-0 absolute bottom-0 z-30`}>
									<button aria-label={playButtonText} className={popupPlayButton()}>
										<PlayIcon />
									</button>
								</div>
							}
							children={
								<MuxVideo {...muxVideoSettings} className={`w-full max-w-none h-full object-cover`} controls={props.controls ?? true} />
							}
							nestedPopup={props.nestedPopup}
						/>
					) : (
						<>
							{!videoLoaded && (
								<div className={`items-center grid h-full left-0 absolute top-0 w-full transition-all z-30`}>
									<Button
										aria-label={playButtonText}
										className={`self-end col-start-1 row-start-1 justify-self-start m-6 transition-all z-40 before:content-[""] before:inset-x-0 before:inset-y-0 before:absolute`}
										onClick={playVideo}
										customIcon={'play'}
										parent={'VideoPlayer'}
									>
										{playButtonText}
									</Button>
								</div>
							)}

							{videoLoaded && (
								<div className={`h-full left-0 absolute top-0 w-full z-30`}>
									<MuxVideo {...muxVideoSettings} className={`w-full max-w-none h-full object-cover`} controls={props.controls ?? true} />
								</div>
							)}
						</>
					)}
				</>
			)}
		</div>
	);
}
